/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    array,
    bool,
    func,
    number,
    object,
    string,
} from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import style from './NextButton.module.css';
import GraphqlFoodAddToCart from '../../../GraphqlFoodAddToCart/GraphqlFoodAddToCart';
import byoTheme from './BYOTheme';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { clearAddToCartError } from '../../../../../../../../state/ducks/AddToCart/AddToCart-Actions';

const NextButton = ({
    selectedSku,
    nextStep,
    prevStep,
    activeStep,
    totalProductsSelected,
    stepsLength,
    maxSelectable,
    addons,
    deliveryMethod,
    handleMobileAgeVerification,
    hasWine,
    isBandItemSelected,
    product,
    cyoSelectedComponents,
    brandId,
    selectedItemId,
    handleMiniCartModalClick,
    disableDecorationBand,
    setShowProgressBar,
    directSteps,
    skuData,
}) => {
    useStyles(style);
    const dispatch = useDispatch();
    const location = useLocation();
    const brandData = useSelector(getBrand);
    const brandCode = brandData?.code;
    const disabledButton = totalProductsSelected !== maxSelectable();
    useEffect(() => {
        if (activeStep === stepsLength) {
            dispatch(clearAddToCartError());
        }
    }, [activeStep, stepsLength]);
    // default color assigned when no brand code found
    const {
        nextButtonColor = '#ffffff',
        nextButtonBg = '#a1001a',
        previousButtonColor = '#a1001a',
    } = byoTheme[brandCode] || {};
    // Gets the value of sale price from the sku.
    const renderSkuPrice = (item) => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const retailPrice = item?.prices?.find((priceData) => priceData.type === 'retail')?.value;
        const salePrice = item?.prices?.find((priceData) => priceData.type === 'sale')?.value;
        const defaultPrice = `$${item?.prices?.[0]?.value}`;
        const offPrice = `${round(retailPrice - salePrice)} off` || '';
        return (
            <span className={style.setItemLayout}>
                {salePrice && retailPrice && salePrice !== retailPrice
                    ? (
                        <>
                            <s>
                                ${retailPrice}
                            </s>
                            <span className={style.salePrice}>
                                ${salePrice}
                            </span>
                            <span className={style.offPrice}>
                                ${offPrice}
                            </span>

                        </>
                    )
                    : <span className={style.defaultPrice}> {defaultPrice}</span>}
            </span>
        );
    };

    const handleScroll = () => {
        if (typeof window !== 'undefined') {
            const header = document.querySelector('#global_header_container');
            const bodyHeight = document.querySelector('#main_body');
            const yotpoHeight = document.querySelector('#yotpo');
            const totalHeight = bodyHeight?.offsetHeight + header?.offsetHeight - window.innerHeight - yotpoHeight?.offsetHeight;
            const containerHeight = totalHeight;
            setShowProgressBar(containerHeight >= window.scrollY);
        }
    };
    useEffect(() => {
        // Add scroll event when the component is loaded
        if (typeof document !== 'undefined') {
            document.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (typeof document !== 'undefined') {
                document.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);
    return (
        <>
            <p className={style.price}>Total {renderSkuPrice(selectedSku)}</p>
            <div
                className={(activeStep > 1 && activeStep !== stepsLength) ? style.buttonContainer : style.buttonWrapper}
                style={{
                    '--btn-next-color': nextButtonColor,
                    '--btn-next-bg': nextButtonBg,
                    '--btn-previous-color': previousButtonColor,
                }}
            >
                {activeStep < stepsLength
                && (
                    <>
                        {activeStep !== 1 &&  (
                            <button
                                type="button"
                                color="primary"
                                id="Previous_Button"
                                className={`${style.previousButton} ${style.nextStepButton}`}
                                tabIndex={-1}
                                onClick={() => {
                                    prevStep(activeStep - 1, false);
                                    dispatch(trackEvent({
                                        eventCategory: 'BYO',
                                        eventAction: 'Previous button Clicked',
                                        eventLabel: `Navigated to step #${activeStep - 1}` || '',
                                    }));
                                }}
                            >
                                Previous
                            </button>
                        )}
                        <button
                            type="button"
                            color="primary"
                            id="Next_Button"
                            disabled={(disabledButton && activeStep === directSteps) || (skuData?.length > 1 && activeStep === 1 && !selectedSku) || (activeStep === directSteps + 1 && disableDecorationBand)}
                            className={activeStep === 1 ? style.nextStepButton : `${style.nextButton} ${style.nextStepButton}`}
                            tabIndex={-1}
                            onClick={() => {
                                if ((!disabledButton && activeStep === directSteps) || (skuData?.length > 1 && activeStep === 1 && selectedSku) || (directSteps + 1 === activeStep && !disableDecorationBand))   {
                                    nextStep(activeStep);
                                    dispatch(trackEvent({
                                        eventCategory: 'BYO',
                                        eventAction: 'Next button Clicked',
                                        eventLabel: `Navigated to step #${activeStep + 1}` || '',
                                    }));

                                    dispatch(trackEvent({
                                        eventCategory: 'BYO – Complete Step',
                                        eventAction: activeStep,
                                        eventLabel: selectedSku?.id || '',
                                        eventName: 'BYO_step_complete',
                                        step: activeStep,
                                        byo_variant: selectedSku?.id || '',
                                    }));
                                }
                            }}
                        >
                            {activeStep === 1 ? 'Next Step' : 'Next'}
                        </button>
                    </>
                )}
                {activeStep === stepsLength &&  (
                    <GraphqlFoodAddToCart
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        selectedItemId={selectedItemId}
                        brandId={brandId}
                        productType="cyo"
                        disableAddToCart
                        totalProductSelected={totalProductsSelected}
                        maxComponents={maxSelectable()}
                        /*
                        FORMAT:
                        {
                            brandCode: brandId,
                            productCode: id,
                            productId: legacyId,
                            quantity: number,
                            componentType: 'CYO'
                        }
                    */
                        cyoSelectedComponents={cyoSelectedComponents()}
                        partNumber={product.partNumber}
                        product={product}
                        // If this is a band product, use functionality, otherwise do not
                        isBandItemSelected={isBandItemSelected}
                        hasWine={hasWine}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        deliveryMethod={deliveryMethod}
                        deliveryIndicator=""
                        addons={addons}
                        categoryId={location?.state?.categoryId || ''}
                        categoryPath={location?.state?.categoryPath || ''}
                        categoryName={location?.state?.categoryName || ''}
                        isBYOProduct
                        atcCallBack={() => {
                            dispatch(trackEvent({
                                eventCategory: 'BYO – Complete Step',
                                eventAction: activeStep,
                                eventLabel: selectedSku?.id || '',
                                eventName: 'BYO_step_complete',
                                step: activeStep,
                                byo_variant: selectedSku?.id || '',
                            }));
                        }}
                    />
                )}
            </div>
        </>
    );
};

NextButton.propTypes = {
    selectedSku: object.isRequired,
    nextStep: func.isRequired,
    setShowProgressBar: func.isRequired,
    prevStep: func.isRequired,
    activeStep: number.isRequired,
    totalProductsSelected: number.isRequired,
    stepsLength: number.isRequired,
    maxSelectable: func.isRequired,
    // add to cart props
    brandId: string.isRequired,
    product: object.isRequired,
    addons: object.isRequired,
    hasWine: bool.isRequired,
    handleMiniCartModalClick: func.isRequired,
    deliveryMethod: object.isRequired,
    selectedItemId: string.isRequired,
    cyoSelectedComponents: object.isRequired,
    isBandItemSelected: bool.isRequired,
    handleMobileAgeVerification: func.isRequired,
    disableDecorationBand: bool.isRequired,
    directSteps: number,
    skuData: array,
};
NextButton.defaultProps = {
    directSteps: 2,
    skuData: [],
};
export default NextButton;
